'use client';

import React, { useRef, useEffect, useState } from 'react';
import styles from './FAQSection.module.css';

interface FAQ {
    question: string;
    answer: string;
}

interface FAQSectionProps {
    title?: React.ReactNode;
    subtitle?: string;
    faqs: FAQ[];
}

export default function FAQSection({
    title = <>Frequently Asked <span className={styles.titleAccent}>Questions</span></>,
    subtitle = "Expert answers to all your questions about setting up a business in Dubai and the UAE.",
    faqs
}: FAQSectionProps) {
    const trackRef = useRef<HTMLDivElement>(null);
    const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isHoveringRef = useRef(false);

    // Handle Scroll
    const scroll = (direction: 'left' | 'right') => {
        if (trackRef.current) {
            const scrollAmount = 424; // Card width (400) + gap (24)
            trackRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth',
            });
            resetAutoScroll();
        }
    };

    const startAutoScroll = () => {
        stopAutoScroll();
        autoScrollIntervalRef.current = setInterval(() => {
            if (trackRef.current && !isHoveringRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = trackRef.current;
                const maxScroll = scrollWidth - clientWidth;
                const scrollAmount = 424;

                if (scrollLeft >= maxScroll - 5) {
                    trackRef.current.scrollTo({ left: 0, behavior: 'smooth' });
                } else {
                    trackRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
                }
            }
        }, 3000);
    };

    const stopAutoScroll = () => {
        if (autoScrollIntervalRef.current) {
            clearInterval(autoScrollIntervalRef.current);
        }
    };

    const resetAutoScroll = () => {
        stopAutoScroll();
        if (!isHoveringRef.current) {
            startAutoScroll();
        }
    };

    useEffect(() => {
        startAutoScroll();
        return () => stopAutoScroll();
    }, []); // Empty dependency array to run once

    const handleMouseEnterTrack = () => {
        isHoveringRef.current = true;
        stopAutoScroll();
    };

    const handleMouseLeaveTrack = () => {
        isHoveringRef.current = false;
        startAutoScroll();
    };

    return (
        <section className={styles.section} aria-label="Frequently Asked Questions">
            <div className={styles.header}>
                <h2 className={styles.title}>{title}</h2>
                {subtitle && <p className={styles.description}>{subtitle}</p>}
            </div>

            <div className={styles.shell}>
                <div className={styles.nav}>
                    <button
                        className={styles.navBtn}
                        onClick={() => scroll('left')}
                        aria-label="Previous questions"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                    </button>
                    <button
                        className={styles.navBtn}
                        onClick={() => scroll('right')}
                        aria-label="Next questions"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                    </button>
                </div>

                <div
                    className={styles.track}
                    ref={trackRef}
                    onMouseEnter={handleMouseEnterTrack}
                    onMouseLeave={handleMouseLeaveTrack}
                >
                    {faqs.map((faq, index) => (
                        <FAQCard key={index} faq={faq} index={index} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function FAQCard({ faq, index }: { faq: FAQ, index: number }) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <article
            ref={cardRef}
            className={`${styles.card}`}
            onMouseMove={handleMouseMove}
        >
            <div
                className={styles.cardGlow}
                style={{ left: mousePos.x, top: mousePos.y }}
            />
            <div className={styles.num}>{String(index + 1).padStart(2, '0')}</div>

            <div className={styles.stack}>
                <h3 className={styles.question}>{faq.question}</h3>
                <p className={styles.answer}>{faq.answer}</p>
            </div>

            <div className={styles.footer}>
                <div className={styles.plus}>+</div>
            </div>
        </article>
    );
}
